"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import FormInput from "../../../components/FormInput";
import Dropdown from "../../../components/Dropdown";
import Button from "../../../components/Button";
import FileInput from "../../../components/FileInput";
import ErrorModal from "../../../components/ErrorModal";
import styles from "../css/dashboard.module.css";
import formStyles from "../css/addbranch.module.css";
import { getBranchByCode as apiGetBranchByCode, updateBranch as apiUpdateBranch, uploadBranchDoc } from "../../../api/branchManagement]/branchApi";

export default function EditBranch() {
  const navigate = useNavigate();
  const location = useLocation();
  const branchcodeFromState = location?.state?.branchcode;
  const [formData, setFormData] = useState({
    branch_name: "",
    branch_code: branchcodeFromState || "",
    pharmacy_license: "",
    address: "",
    suburb: "",
    province: "",
    province_dropdown: "",
    postal_code: "",
    country: "",
    telephone: "",
    email: "",
    cert_doc: null,
    dispensing_fee: "",
    existing_doc_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!branchcodeFromState) return;
      try {
        setLoading(true);
        const b = await apiGetBranchByCode(branchcodeFromState);
        setFormData({
          branch_name: b.branchname || "",
          branch_code: b.branchcode || "",
          pharmacy_license: b.pharmacylicenceno || "",
          address: b.address || "",
          suburb: b.suburb || "",
          province: b.province || "",
          province_dropdown: b.province || "",
          postal_code: b.postalcode || "",
          country: b.country || "",
          telephone: b.telephoneno || "",
          email: b.email || "",
          cert_doc: null,
          dispensing_fee: b.dispensingfee != null ? String(b.dispensingfee) : "",
          existing_doc_url: b.docurl || "",
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [branchcodeFromState]);

  const handleChange = (name) => (e) => {
    if (name === "cert_doc") {
      setFormData({ ...formData, [name]: e.target.files[0] });
    } else {
      let value = e.target.value;
      if (name === "telephone") {
        // Allow only digits, max 10
        value = value.replace(/\D/g, "").slice(0, 10);
      }
      setFormData({ ...formData, [name]: value });
    }
  };

  // Upload handled via API-layer helper

  const handleSubmit = (e) => {
    e.preventDefault();
    (async () => {
      try {
        setSaving(true);
        let docurl = undefined;
        if (formData.cert_doc && formData.branch_code) {
          docurl = await uploadBranchDoc(formData.branch_code, formData.cert_doc);
        }
        const payload = {
          branchname: formData.branch_name,
          pharmacylicenceno: formData.pharmacy_license,
          address: formData.address,
          suburb: formData.suburb,
          province: formData.province_dropdown || formData.province,
          postalcode: formData.postal_code,
          country: formData.country,
          telephoneno: formData.telephone,
          email: formData.email,
          ...(docurl ? { docurl } : {}),
          dispensingfee: formData.dispensing_fee === "" ? null : Number(formData.dispensing_fee),
        };
        await apiUpdateBranch(formData.branch_code, payload);
        navigate(-1);
      } catch (e) {
        console.error(e);
        const rawMessage =
          (e && e.response && e.response.data && e.response.data.message) ||
          e?.message ||
          "Something went wrong while updating branch";

        const msg = String(rawMessage || "");

        if (
          msg.includes("Unique constraint failed") &&
          msg.includes("branchcode")
        ) {
          setErrorMessage("A branch with this Branch Code already exists. Please use a different Branch Code.");
        } else if (
          msg.includes("Unique constraint failed") &&
          msg.toLowerCase().includes("email")
        ) {
          setErrorMessage("This email address is already linked to another branch. Please use a different email address.");
        } else {
          setErrorMessage(rawMessage);
        }
        setShowError(true);
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <main className={styles.content}>
          <h1 className={formStyles.pageTitle}>Edit Branch</h1>

          <form className={formStyles.formContainer} onSubmit={handleSubmit}>
            
            {/* --- First Section --- */}
            <div className={formStyles.formRowFourFields}>
              <FormInput
                label="Branch Name"
                name="branch_name"
                value={formData.branch_name}
                onChange={handleChange("branch_name")}
              />
              <FormInput
                label="Branch Code"
                name="branch_code"
                value={formData.branch_code}
                onChange={handleChange("branch_code")}
              />
              <FormInput
                label="Pharmacy Practice Number"
                name="pharmacy_license"
                value={formData.pharmacy_license}
                onChange={handleChange("pharmacy_license")}
              />
              <FormInput
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange("address")}
              />
            </div>
            <hr className={formStyles.sectionDivider} />

            {/* --- Second Section --- */}
            <div className={formStyles.formRowFourFields}>
              <FormInput
                label="Suburb"
                name="suburb"
                value={formData.suburb}
                onChange={handleChange("suburb")}
              />
              <Dropdown
                label="Province"
                name="province"
                options={[
                  "Eastern Cape",
                  "Free State",
                  "Gauteng",
                  "KwaZulu-Natal",
                  "Limpopo",
                  "Mpumalanga",
                  "Northern Cape",
                  "North West",
                  "Western Cape",
                ]}
                value={formData.province_dropdown}
                onChange={(e) => setFormData({ ...formData, province_dropdown: e.target.value })}
              />
              <FormInput
                label="Postal Code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange("postal_code")}
              />
            </div>
            <hr className={formStyles.sectionDivider} />

            {/* --- Third Section --- */}
            <div className={formStyles.formRowFourFields}>
              <FormInput
                label="Country"
                name="country"
                value={formData.country || "South Africa"}
                disabled
                className={formStyles.compactField}
              />
              <FormInput
                label="Telephone Number"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange("telephone")}
                placeholder="Enter 10-digit number"
              />
              <FormInput
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange("email")}
              />
              <FormInput
                label="Dispensing Fee"
                type="number"
                name="dispensing_fee"
                value={formData.dispensing_fee}
                onChange={(e) => {
                  const v = e.target.value;
                  const num = Number(v);
                  if (!Number.isFinite(num)) {
                    setFormData({ ...formData, dispensing_fee: "" });
                  } else {
                    setFormData({ ...formData, dispensing_fee: num < 0 ? "0" : v });
                  }
                }}
                placeholder="0.00"
                prefix="R"
                min={0}
                step={1}
              />
            </div>
            <div className={formStyles.formRow}>
              <FileInput
                label="Certification Document (replace)"
                name="cert_doc"
                id="cert_doc"
                value={formData.cert_doc}
                onChange={handleChange("cert_doc")}
                accept=".pdf,.doc,.docx"
                wide={true}
                existingUrl={formData.existing_doc_url}
              />
            </div>

            {/* Action Buttons */}
            <div className={formStyles.formActions}>
              <Button variant="primary" type="submit" disabled={saving || loading}>Save</Button>
              <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            </div>
          </form>
        </main>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
      <ErrorModal
        isOpen={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />
    </div>
  );
}